import type { Translation } from '../types.ts';
import type en from '../en/stats.ts';

export default {
    shared: {
        views: 'Weergaven',
        uniqueVisitors: 'Unieke bezoekers',
        uniques: 'Unieke bezoekers',
        sessions: 'Sessies',
        avgDwell: 'Gem. verblijftijd',
        botViews: 'Botweergaven',
        bounce: 'Bounce',
        path: 'Pad',
        source: 'Bron',
        orders: 'Bestellingen',
        revenue: 'Omzet',
        conversionRate: 'Conversieratio',
        formSubmissions: 'Leads',
        clientErrors: 'Clientfouten (4xx)',
        serverErrors: 'Serverfouten (5xx)',
        redirects: 'Omleidingen (3xx)',
        direct: '(direct)',
        none: '(geen)',
        unknown: '(onbekend)',
        viewAll: 'Alles bekijken',
        noDataYet: 'Nog geen gegevens',
        noDataRange: 'Geen gegevens in deze periode.',
    },
    units: {
        milliseconds: '{n} ms',
        seconds: '{n} s',
        minutesSeconds: '{m} min {s} s',
    },
    nav: {
        label: 'Statistieken',
        overview: 'Overzicht',
        pages: "Pagina's",
        referrers: 'Doorverwijzers',
        geo: 'Geografie',
        technology: 'Apparaten',
        campaigns: 'Campagnes',
        conversions: 'Conversies',
        errors: 'Fouten',
    },
    range: {
        preset: '{n}d',
    },
    change: {
        new: 'Nieuw',
        noChange: 'Ongewijzigd',
        vsPrevious: 't.o.v. vorige dag | t.o.v. vorige {n} dagen',
        previous: 'Vorige dag: {value} | Vorige {n} dagen: {value}',
    },
    attribution: {
        viewsTitle: 'Alle weergaven in deze sessies',
        bounceTitle: 'Sessies met één weergave',
        convRate: 'Conv.ratio',
        rateOrders: 'Betaalde bestellingen per sessie',
        rateForms: 'Leads (formulieren, bellen, WhatsApp, e-mail) per sessie',
        revenueTitle: 'Betaalde bestellingen, zonder terugbetalingen',
        forms: 'Leads',
    },
    conversionTiles: {
        notApplicable: 'n.v.t.',
        ofSessions: '{rate} van de sessies',
        phoneTaps: 'Tikken op telefoonnummer',
        whatsappTaps: 'Tikken op WhatsApp',
    },
    conversionChart: {
        revenue: 'Omzet: {amount}',
    },
    shareList: {
        tooltip: '{visitors} unieke bezoekers, {sessions} sessies',
    },
    worldMap: {
        label: 'Bezoekers per land',
        hover: '{views} weergaven · {uniques} unieke bezoekers',
        noVisitorsRange: 'Geen bezoekers in deze periode',
        noVisitors: 'Geen bezoekers',
        unmapped: 'Te klein om op deze schaal te tekenen, zie de tabel: {countries}',
    },
    overview: {
        title: 'Statistieken',
        botTraffic: 'Botverkeer',
        transferred: 'Dataverkeer',
        trafficComparison: 'Verkeer vergeleken',
        dailyTrend: 'Trend per dag',
        compare: 'Vergelijken',
        dailyBreakdown: 'Per dag',
        day: 'Dag',
        topPages: "Toppagina's",
        topReferrers: 'Top doorverwijzers',
        topCountries: 'Toplanden',
        topErrors: "Toppagina's zonder 2xx",
        bounce: '{pct} bounce',
    },
    pages: {
        title: "Toppagina's",
        entryHeading: 'Waar sessies beginnen',
        pagesHeading: "Pagina's op weergaven",
        allPages: "Alle pagina's",
        entryPages: "Instappagina's",
        entryPage: 'Instappagina',
        entries: 'Instappen',
        avgScroll: 'Gem. scrolldiepte',
        avgScrollTitle: 'Hoe ver bezoekers gemiddeld naar beneden scrolden',
    },
    referrers: {
        title: 'Top doorverwijzers',
        referrers: 'Doorverwijzers',
        referrerHost: 'Host van doorverwijzer',
        outbound: 'Uitgaande links',
        destinationHost: 'Doelhost',
        clicks: 'Klikken',
        noOutbound: 'Geen uitgaande klikken in deze periode.',
    },
    geo: {
        title: 'Geografie',
        whereVisitorsAre: 'Waar bezoekers vandaan komen',
        countries: 'Landen',
        cities: 'Steden',
        location: 'Locatie',
        countryOnly: '(alleen land)',
    },
    technology: {
        title: 'Apparaten',
        devices: 'Apparaten',
        browsers: 'Browsers',
        os: 'Besturingssystemen',
        languages: 'Sitetalen',
        languagesHint: 'De taalversie van de site die werd bezocht, niet de taal van de browser.',
        device: {
            desktop: 'Desktop',
            smartphone: 'Smartphone',
            tablet: 'Tablet',
            phablet: 'Phablet',
            featurePhone: 'Featurephone',
            console: 'Spelcomputer',
            tv: 'Tv',
            carBrowser: 'Autobrowser',
            smartDisplay: 'Slim scherm',
            smartSpeaker: 'Slimme speaker',
            camera: 'Camera',
            mediaPlayer: 'Draagbare mediaspeler',
            wearable: 'Wearable',
            peripheral: 'Randapparaat',
        },
    },
    campaigns: {
        title: 'Campagnes',
        empty: 'Geen campagneverkeer in deze periode.',
        emptyHint: 'Voeg UTM-parameters toe aan de links die je deelt, bijvoorbeeld {example}. Bezoeken die via zo\'n link binnenkomen, worden hier gegroepeerd op bron, medium en campagne.',
        intro: 'Sessies worden gegroepeerd op de UTM-parameters van de pagina waarop ze binnenkwamen. "{none}" zijn alle sessies waarvan de landingspagina zo\'n parameter niet had.',
        sources: 'Bronnen',
        mediums: 'Media',
        campaigns: 'Campagnes',
        medium: 'Medium',
        campaign: 'Campagne',
    },
    conversions: {
        title: 'Conversies',
        ordersNote: 'Bestellingen tellen mee zodra ze betaald zijn; terugbetaalde bestellingen tellen niet mee. De omzet is het totaalbedrag van de bestelling.',
        formsNote: 'Een lead is een formulierinzending, geteld zodra die is opgeslagen, of een tik op het telefoonnummer, het e-mailadres of de WhatsApp-knop. Elke lead wordt toegeschreven aan de verkeersbron en landingspagina van het bezoek waar die vandaan kwam.',
        daily: 'Conversies per dag',
        bySource: 'Per verkeersbron',
        byLandingPage: 'Per landingspagina',
        byForm: 'Per formulier of contactknop',
        landingPage: 'Landingspagina',
        form: 'Formulier of knop',
        submissions: 'Leads',
        unnamed: '(naamloos)',
        noForms: 'Geen leads in deze periode.',
        contact: {
            phone: 'Telefoonnummer aangetikt',
            email: 'E-mailadres aangetikt',
            whatsapp: 'WhatsApp aangetikt',
        },
    },
    errors: {
        title: 'Fouten',
        overTime: 'Statuscodes in de tijd',
        byStatus: "Pagina's per status",
        classOnly: 'alleen {label}',
        showAll: 'Alles tonen',
        hits: 'Hits',
        noneOfClass: 'Geen {label}-responses in deze periode.',
        noneAtAll: 'Geen omleidingen of fouten in deze periode. 🎉',
    },
} satisfies Translation<typeof en>;
